import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { paymentFrequencyOptions } from '@/modules/liabilities/LiabilityModal/LiabilityPaymentDetailsForm.constants';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EstateWaterfall_TransferLogItemFragment } from '../../../graphql/EstateWaterfall.generated';
import { EdgeLabelCard } from '../edgeLabelCard.component';
import { CardConfig } from './types';

interface LiabilityCardParams {
  sourceEntityName: string;
  targetEntityName: string;
  // For when the type is LiabilityPaymentOnDeath
  liability?: EstateWaterfall_TransferLogItemFragment;
  // For when the type is LiabilityPayment
  payments?: EstateWaterfall_TransferLogItemFragment[];
}

export function createLiabilityCard({
  sourceEntityName,
  targetEntityName,
  liability: externalLiability,
  payments,
}: LiabilityCardParams): CardConfig {
  const isPayment = !!payments;

  const liability = externalLiability ?? payments?.[0];

  const associatedLiability = (() => {
    if (liability?.associatedObject?.__typename === 'Liability') {
      return liability?.associatedObject;
    }

    return null;
  })();

  const paymentAmount = isPayment
    ? (associatedLiability?.recurringPaymentAmount ?? new Decimal(0))
    : (liability?.finalTransferAmount ?? new Decimal(0));

  const remainingBalanceNow =
    associatedLiability?.currentAmount ?? new Decimal(0);

  const remainingBalanceAtDeath =
    liability?.desiredTransferAmount?.minus(paymentAmount) ?? new Decimal(0);

  const frequencyDisplay = isPayment
    ? paymentFrequencyOptions.find(
        (option) => option.value === associatedLiability?.paymentFrequency
      )?.display
    : null;

  const termEnd = associatedLiability?.termEnd;

  const debtor = sourceEntityName;
  const lender = targetEntityName;

  const description = !!payments
    ? `Total liability payments made by ${debtor} to ${lender}. Liability will be repaid in full upon death if grantor dies before liability term end.`
    : `${debtor} repays ${lender} the remaining balance of the liability upon grantor’s death.`;

  return {
    element: (
      <EdgeLabelCard
        key={`pourover-${sourceEntityName}-${targetEntityName}`}
        title={isPayment ? 'Liability payment' : 'Liability repayment'}
        description={description}
        rows={compact([
          remainingBalanceNow.gte(0)
            ? {
                label: `Remaining balance (${new Date().getFullYear()})`,
                value: formatCurrencyNoDecimals(remainingBalanceNow),
              }
            : null,
          paymentAmount.gt(0)
            ? {
                label: 'Payment amount',
                value: formatCurrencyNoDecimals(paymentAmount),
              }
            : null,
          frequencyDisplay
            ? {
                label: 'Frequency',
                value: frequencyDisplay,
              }
            : null,
          termEnd && isPayment
            ? {
                label: 'Term end',
                value: termEnd.getFullYear().toString(),
              }
            : null,
          isPayment
            ? null
            : {
                label: `Remaining balance (${liability?.year})`,
                value: formatCurrencyNoDecimals(remainingBalanceAtDeath),
              },
        ])}
      />
    ),
    label: !!payments ? 'Liability' : 'Repayment',
    value: formatCurrencyNoDecimals(
      liability?.finalTransferAmount ?? new Decimal(0)
    ),
    variant: 'primary',
  };
}
